import { sleep } from '@ipfabric/ui-components/utils/sleep';
import type { Bounds, EdgeId, NodeId } from '@ipfabric/utils';
import { branch, exec } from '@ipfabric/utils/fp/function';
import { prop } from '@ipfabric/utils/fp/prop';
import type { Viewport } from 'pixi-viewport';
import { type Application, Container } from 'pixi.js';
import { type FC, PureComponent } from 'react';

import './styles.css';
import { isFeatureFlagEnabled } from '@ipfabric/ui-components/utils/featureFlags';
import type { GraphEdgeDisplayObject } from 'components/Graph/GraphApp/GraphEdge';
import type { GraphNodeDisplayObject } from 'components/Graph/GraphApp/GraphNode';
import type { GraphAppEvents } from 'components/Graph/GraphApp/events/GraphAppEvents';
import { createBindCommandEventFunctions } from 'components/Graph/GraphApp/helpers/createBindCommandEventFunctions';
import type {
  GraphRendererPlugin,
  GraphRendererPluginInterface,
} from 'components/Graph/GraphApp/plugins/GraphRendererPlugin';
import { boundsObserver } from 'components/Graph/GraphApp/plugins/boundsObserver';
import { clampZoom } from 'components/Graph/GraphApp/plugins/clampZoom';
import { cull } from 'components/Graph/GraphApp/plugins/cull';
import { disableTextSelection } from 'components/Graph/GraphApp/plugins/disableTextSelection';
import { edgeHighlight } from 'components/Graph/GraphApp/plugins/edgeHighlight';
import { fitContentToViewport } from 'components/Graph/GraphApp/plugins/fitContentToViewport';
import { hiddenNodes } from 'components/Graph/GraphApp/plugins/hiddenNodes';
import { hideLabelsOnFarZoom } from 'components/Graph/GraphApp/plugins/hideLabelsOnFarZoom';
import { highlightGraphPart } from 'components/Graph/GraphApp/plugins/highlightGraphPart';
import { highlightPacketPath } from 'components/Graph/GraphApp/plugins/highlightPacketPath';
import { highlightRelated } from 'components/Graph/GraphApp/plugins/highlightRelated';
import { nodeBoxes } from 'components/Graph/GraphApp/plugins/nodeBoxes';
import { nodeClick } from 'components/Graph/GraphApp/plugins/nodeClick';
import { nodeContextMenu } from 'components/Graph/GraphApp/plugins/nodeContextMenu';
import { nodeDrag } from 'components/Graph/GraphApp/plugins/nodeDrag';
import { nodeSelection } from 'components/Graph/GraphApp/plugins/nodeSelection';
import { renderData } from 'components/Graph/GraphApp/plugins/renderData';
import { renderOnViewportChange } from 'components/Graph/GraphApp/plugins/renderOnViewportChange';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import { edgeMouseOut } from 'components/Graph/commands/edgeMouseOut';
import { edgeMouseOver } from 'components/Graph/commands/edgeMouseOver';
import { finishRender } from 'components/Graph/commands/rendering/finishRender';
import type { GraphData } from 'components/Graph/model/GraphData';
import { option } from 'fp-ts';
import type { Option } from 'fp-ts/lib/Option';
import { pipe } from 'fp-ts/lib/function';
import { dumpCallStackTop } from 'utils/debug/dumpCallStackTop';
import type { TypedEventEmitter } from 'utils/events/TypedEventEmitter';
import { createAnimationController } from 'utils/pixi/animation/AnimationController';
import {
  type CommandDispatcher,
  curryDispatch,
} from 'utils/react/useCommandDispatcher';
import type { GraphMetadata, Pathlookup, SearchResult } from '../GraphState';
import type { GraphCommandContext } from '../model/GraphCommandContext';
import { edgeClick } from './plugins/edgeClick';
import { edgeContextMenu } from './plugins/edgeContextMenu';
import { type GraphAppHandleRef, externalApi } from './plugins/externalApi';
import { fitEdgeToView } from './plugins/fitEdgeToView';
import { fitNodeToView } from './plugins/fitNodeToView';
import { nodeHighlight } from './plugins/nodeHighlight';
import { pathLookupPathInspector } from './plugins/pathLookupPathInspector';
import { wheelMove } from './plugins/wheelMove';
import { zoom } from './plugins/zoom';

interface Props {
  app: Application;
  appEvents: TypedEventEmitter<GraphAppEvents>;
  isSelectionMode: boolean;
  viewport: Viewport;
}

interface InnerProps extends Props {
  dispatch: CommandDispatcher<GraphCommandContext>;
  graph: GraphData;
  graphAppHandleRef?: GraphAppHandleRef;
  graphMetadata: GraphMetadata | null;
  pathlookup: Pathlookup;
  peekingAtHiddenNodes: boolean;
  searchResult: SearchResult | null;
}

const CONTROLS_LAYER_NAME = 'controlsLayer';
const GRAPH_LAYER_NAME = 'graphLayer';

/**
 * This class handles rendering and interactions of canvas part of graph.
 *
 * Any common graph HTML controls - buttons, forms etc. should be managed by Graph or its other subcomponents.
 * Althrough we prefer function components with hooks, we keep this as class component since it uses
 * a number of instance variables and callbacks that would be not so handy and well readable in function components.
 */
class BareGraphRenderer extends PureComponent<InnerProps> {
  static whyDidYouRender = true;

  private mounted = false;

  private graphBounds: Option<Bounds> = option.none;

  private renderRequestPromise: Promise<void> | null = null;

  private requestRendenderId: number | null = null;

  async componentDidMount(): Promise<void> {
    this.mounted = true;
    const { props } = this;

    // let rendering loading state be displayed
    await sleep(0);
    if (!this.mounted) {
      return;
    }

    props.viewport.sortableChildren = true;
    const controlsLayer = new Container();
    controlsLayer.name = CONTROLS_LAYER_NAME;
    const graphLayer = new Container();
    graphLayer.name = GRAPH_LAYER_NAME;
    graphLayer.sortableChildren = true;
    props.viewport.addChild(graphLayer);
    props.viewport.addChild(controlsLayer);

    this.initPlugins();

    this.requestRender();
  }

  private initPlugins() {
    const cdispatch = curryDispatch(this.props.dispatch);
    const pluginInterface: GraphRendererPluginInterface = {
      animate: pipe(
        createAnimationController(this.requestRender),
        branch(({ cancelAll }) => {
          this.props.appEvents.addListener('beforeUnmount', cancelAll);
        }),
        ({ animate }) => animate,
      ),
      app: this.props.app,
      ...createBindCommandEventFunctions(
        this.props.dispatch,
        this.props.appEvents,
      ),
      controlsLayer: this.props.viewport.getChildByName(
        CONTROLS_LAYER_NAME,
      ) as Container,
      edgeDisplayObjects: new Map<EdgeId, GraphEdgeDisplayObject>(),
      events: this.props.appEvents,
      getGraphBounds: () => this.graphBounds,
      getGraphData: () => this.props.graph,
      getGraphMetadata: () => this.props.graphMetadata,
      getPathlookup: () => this.props.pathlookup,
      getSearchResult: () => this.props.searchResult,
      graphDispatch: this.props.dispatch,
      graphLayer: this.props.viewport.getChildByName(
        GRAPH_LAYER_NAME,
      ) as Container,
      isPeekingAtHiddenNodes: () => this.props.peekingAtHiddenNodes,
      isSelectionMode: () => this.props.isSelectionMode,
      nodeDisplayObjects: new Map<NodeId, GraphNodeDisplayObject>(),
      onEdgeMouseOut: cdispatch(edgeMouseOut),
      onEdgeMouseOver: cdispatch(edgeMouseOver),
      requestRender: this.requestRender,
      viewport: this.props.viewport,
    };
    const plugins: GraphRendererPlugin[] = [
      boundsObserver,
      clampZoom,
      cull,
      disableTextSelection,
      edgeHighlight,
      pathLookupPathInspector,
      edgeClick,
      edgeContextMenu,
      externalApi(this.props.graphAppHandleRef),
      fitContentToViewport,
      fitEdgeToView,
      fitNodeToView,
      hiddenNodes,
      hideLabelsOnFarZoom,
      highlightGraphPart,
      highlightPacketPath,
      highlightRelated,
      nodeHighlight,
      nodeBoxes,
      nodeClick,
      nodeContextMenu,
      nodeDrag,
      nodeSelection,
      renderData,
      renderOnViewportChange,
      wheelMove,
      zoom,
      // Plugins for debugging purposes follow. Uncomment and import to use.
      // performanceMonitor,
      // snappingGridVisualisation,
    ];
    for (const pluginFactory of plugins) {
      pluginFactory(pluginInterface);
    }
    this.props.appEvents.addListener('graphBoundsChange', ({ newBounds }) => {
      this.graphBounds = newBounds;
    });
    pluginInterface.events.emit('graphDataChange', { prevData: null });
  }

  private renderCallbackQueue: (() => void)[] = [];

  private requestRender = (cb?: () => void): Promise<void> => {
    if (
      process.env.NODE_ENV === 'development' &&
      isFeatureFlagEnabled('ENABLE_GRAPH_DUMPING_RENDERER_REQUEST_SOURCE')
    ) {
      dumpCallStackTop(new Error(), 'requestRender');
    }
    if (cb) {
      this.renderCallbackQueue.push(cb);
    }
    if (!this.renderRequestPromise) {
      this.renderRequestPromise = new Promise((resolve) => {
        this.requestRendenderId = window.requestAnimationFrame(() => {
          this.renderRequestPromise = null;
          this.renderCallbackQueue.forEach(exec);
          this.renderCallbackQueue.splice(0);
          this.props.appEvents.emit('beforeRender');
          this.props.app.render();
          this.props.appEvents.emit('afterRender');
          resolve();
          this.props.dispatch(finishRender);
        });
      });
    }
    return this.renderRequestPromise;
  };

  componentDidUpdate(prevProps: Readonly<InnerProps>): void {
    const { props } = this;

    if (props.graph !== prevProps.graph) {
      props.appEvents.emit('graphDataChange', { prevData: prevProps.graph });
      this.requestRender();
    }

    if (props.graphMetadata !== prevProps.graphMetadata) {
      props.appEvents.emit('graphMetadataChange', {
        previous: prevProps.graphMetadata,
      });
    }

    if (props.peekingAtHiddenNodes !== prevProps.peekingAtHiddenNodes) {
      props.appEvents.emit('peekingAtHiddenNodesChange');
    }

    if (props.searchResult !== prevProps.searchResult) {
      props.appEvents.emit('searchResultChange');
    }
  }

  componentWillUnmount(): void {
    // renderer is optional since app may be already destroyed by parent umnount
    this.props.app.renderer?.plugins.interaction.removeAllListeners();
    if (this.requestRendenderId) {
      cancelAnimationFrame(this.requestRendenderId);
    }
    this.mounted = false;
  }

  public render(): null {
    return null;
  }
}

export const GraphRenderer: FC<Props> = (props: Props) => {
  const ctxProps = useGraphContextSelectors({
    dispatch: prop('dispatch'),
    graph: prop('graph'),
    graphAppHandleRef: prop('graphAppHandleRef'),
    pathlookup: prop('pathlookup'),
    peekingAtHiddenNodes: prop('peekingAtHiddenNodes'),
    graphMetadata: prop('graphMetadata'),
    searchResult: prop('searchResult'),
  });

  return <BareGraphRenderer {...props} {...ctxProps} />;
};
